<template>
    <modal
        :id="modalId"
        title="Inquiry Source Setup"
        size="lg"
        @shown="show"
        @hide="hide"
    >
        <b-row>
            <b-col>
                <b-form-group>
                    <label>Require inquiry source choice?</label><br>
                    <form-square-switch
                        @change="saveInquirySources"
                        v-model="inquirySettings.inquiry_settings.force_manual_source_choice"
                        :labels="{
                            checked: 'ON',
                            unchecked: 'OFF',
                        }"
                        class="require-source-choice-switch"
                        :isToolbar="true"
                    />
                </b-form-group>
                <form-input
                    class="mb-0"
                    :value="selectedDefaultManualInquirySourceLabel"
                    disabled
                    :rightButton="{
                        title: 'Edit',
                        icon: isShowInquirySources ? ['fas', 'caret-up'] : ['fas', 'caret-down'],
                        variant: 'outline-primary',
                        toggle: true,
                    }"
                    @handle-right-button-click="showHideInquirySources"
                    label="Default Source:"
                />
                <b-collapse v-model="isShowInquirySources">
                    <p class="mt-4">Sources</p>
                    <b-input-group v-for="source in inquirySourceOptions" :key="source.id" class="mt-3">
                        <b-input-group-prepend class="answer-wrapper" is-text>
                            <b-form-radio
                                v-model="selectedDefaultInquirySource"
                                :value="source.id"
                                @change="onSelectDefaultInquirySource(source.id)"
                                name="source"
                            ></b-form-radio>
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="source.name === 'Phone'"
                            v-model="source.name"
                            @input="onChangeSourceName"
                        ></b-form-input>
                        <template #append v-if="source.name !== 'Phone'">
                            <b-button variant="default" @click="removeSource(source.id)">
                                <font-awesome-icon :icon="['fas', 'xmark']" />
                            </b-button>
                        </template>
                    </b-input-group>
                </b-collapse>
            </b-col>
        </b-row>
        <template #modal-footer="{ cancel }">
            <div class="w-100">
                <b-button v-if="isShowInquirySources" variant="outline-primary" class="float-left" @click="addSource()">Add Source</b-button>
                <b-button variant="primary" class="float-right" @click="cancel()"> Close </b-button>
            </div>
        </template>        
    </modal>        
</template>
<script>
    import API from '../../../api';
    import debounce from 'lodash/debounce';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import { formatResponseError } from '../../../utils/api';
    import {
        RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT,
    } from '../../../endpoints';    

    export default {
        name: 'InquirySourceSetupModal',
        components: {
            BusyModal,
            Modal,
            FormInput,
            FormSquareSwitch,
            FormSelect,
        },
        props: {
            modalId: String,
            variableType: String,
        },
        data() {
            return {
                busy: false,
                isShowInquirySources: false,
                selectedDefaultInquirySource: null,
                inquirySettings: null,
                inquirySourceOptions: [],
            };
        },
        async mounted() {
            this.busy = true;
            await this.fetchInquirySettings();
            await Promise.all([this.fetchInquirySources()]);
            this.busy = false;                 
        },
        computed: {
            selectedDefaultManualInquirySourceLabel() {
                return this.inquirySourceOptions.find((item) => item.id === this.selectedDefaultInquirySource)?.name;
            },           
        },
        methods: {
            hide() {
                this.$emit('refreshSources', true);
            },
            async saveInquirySources() {
                try {
                    this.busy = true;
                    await API.post(RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT(), {
                        settings: this.inquirySettings.inquiry_settings,
                        sources: this.inquirySourceOptions,
                        selected_default_source: this.selectedDefaultInquirySource,
                    });
                    this.successToast({ text: 'Settings successfully saved' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save inquiries settings' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchInquirySettings() {
                try {
                    this.busy = true;
                    const { data } = await API.get(RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT());
                    this.inquirySettings = data;
                    this.selectedDefaultInquirySource = Number(data.default_manual_inquiry_source);
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch inquiries settings' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchInquirySources() {
                try {
                    const { data } = await API.get(`${RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT()}sources/`);
                    this.inquirySourceOptions = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
    
                    this.errorToast({ text: generic || 'Could not fetch sources' });
                }
            },            
            showHideInquirySources() {
                this.isShowInquirySources = !this.isShowInquirySources;
            },
            onSelectDefaultInquirySource(id) {
                this.selectedDefaultInquirySource = id;
                this.saveInquirySources();
            },                    
            onChangeSourceName: debounce(function () {
                this.saveInquirySources();
            }, 1000),
            async removeSource(id) {
                try {
                    this.busy = true;
                    this.inquirySourceOptions = this.inquirySourceOptions.filter((item) => item.id !== id);
                    if (this.selectedDefaultInquirySource === id) {
                        const defaultSource = this.inquirySourceOptions.find((item) => item.name.toLowerCase() === 'phone');
                        this.onSelectDefaultInquirySource(defaultSource?.id);
                    }
                    await API.delete(`${RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT(id)}delete-source/`);
                } catch (e) {
                    const { generic } = formatResponseError(e);
    
                    this.errorToast({ text: generic || 'Could not delete source' });
                } finally {
                    this.busy = false;
                }
            },
            async addSource() {
                try {
                    this.busy = true;
    
                    const { data } = await API.post(`${RESERVATION_DETAILS_INQUIRY_SOURCES_SETUP_ENDPOINT()}create-source/`, {
                        name: 'New Source',
                    });
                    this.inquirySourceOptions.push({
                        id: data,
                        name: 'New Source',
                    });
                    
                    this.$nextTick(() => {
                        const modalContent = document.querySelector(`#${this.modalId} .modal-body`);
                        if (modalContent) {
                            modalContent.scrollTop = modalContent.scrollHeight;
                        }
                    });                    
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not add source' });
                } finally {
                    this.busy = false;
                }
            },            
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.require-source-choice-switch) {
        width: 80px !important;
    }
</style>


